import { graphql, Link, useStaticQuery } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import React from "react"

import Button from "@components/Button/Button"
import Hero from "@components/Hero/Hero"
import Layout from "@components/Layout/Layout"
import Timeline from "@components/Timeline/Timeline"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card"
import useScrollSpy from "@hooks/useScrollSpy"

function GroupTemplate({ lang, texts }) {
  const { activeSection, sectionRefs } = useScrollSpy(
    texts.asideMenu.map((menu) => menu.to)
  )

  const data = useStaticQuery(graphql`
    {
      hero: file(
        relativePath: { eq: "photo-historique-chantier.jpg" }
        sourceInstanceName: { eq: "images" }
      ) {
        childImageSharp {
          gatsbyImageData(placeholder: DOMINANT_COLOR, layout: FULL_WIDTH)
        }
      }
      famille: file(
        relativePath: { eq: "famille-Fehr.jpg" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
      labo_1: file(
        relativePath: { eq: "Labo_Fehr_Bischwiller_2.jpeg" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
      labo_2: file(
        relativePath: { eq: "laboratoire2.jpg" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
      labo_3: file(
        relativePath: { eq: "Labo_Fehr_Bischwiller_3.jpeg" }
        sourceInstanceName: { eq: "images" }
      ) {
        ...Constrained
      }
    }
  `)
  return (
    <Layout lang={lang} dropdownId="enterprise" routeId="group">
      <Hero
        title={texts.history.heroTitle}
        backgroundHero={data.hero.childImageSharp.gatsbyImageData}
        subtitle={texts.history.heroSubtitle}
      />

      <div className="grid max-w-screen-2xl grid-cols-7">
        <aside className="sticky top-28 hidden h-screen self-start border-r border-gray-100 md:col-span-2 md:block">
          <ul role="list" className="divide-y divide-gray-100">
            {texts.asideMenu.map((it) => (
              <li
                key={it.to}
                className={`flex gap-x-4 px-5 transition-all hover:bg-gray-50/80 ${activeSection === it.to ? "bg-fehr-500 *:text-white" : "*:text"}`}
              >
                {it.target ? (
                  <a
                    href={it.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex h-full flex-auto items-baseline justify-start gap-x-4 px-3 py-5 text-sm font-semibold leading-6"
                  >
                    {it.title}
                    {it.icon && it.icon}
                  </a>
                ) : (
                  <Link to={`#${it.to}`} className="h-full flex-auto px-3 py-5">
                    <div className="flex items-baseline justify-between gap-x-4">
                      <p className="text-sm font-semibold leading-6">
                        {it.title}
                      </p>
                    </div>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </aside>

        <div className="space-y-18 col-span-full px-10 py-8 md:col-span-5 lg:space-y-24">
          {/* Values content */}
          <section ref={sectionRefs["values"]} className="space-y-7">
            <div className="space-y-5">
              {texts.history.main_content.map((content, i) => (
                <p key={i} className="text-base/6">
                  {content}
                </p>
              ))}
              <GatsbyImage
                image={data.famille.childImageSharp.gatsbyImageData}
                alt={texts.history.imageAlt}
                className="rounded-sm"
              />
            </div>

            <div id="values" className="space-y-5">
              <h2 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
                {texts.history.valuesTitle}
              </h2>
              <p>{texts.history.valuesIntro}</p>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-3 xl:my-5 xl:grid-cols-3">
                {texts.history.columnsData.map((column) => (
                  <Card key={column.title} className="space-y-3">
                    <CardHeader>
                      <CardDescription>
                        <column.icon className="size-10 stroke-fehr-500" />
                      </CardDescription>
                      <CardTitle as="h3" className="text-2xl">
                        {column.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul>
                        {column.items.map((item, idx) => (
                          <li key={idx} className="arrow-before capitalize">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* History content */}
          <div id="history" ref={sectionRefs["history"]}>
            <Timeline lang={lang} title={texts.history.timelineTitle} />
          </div>

          {/* R&D content */}
          <section
            id="rd"
            ref={sectionRefs["rd"]}
            className="mx-auto space-y-4 lg:mx-0"
          >
            <h2 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              {texts.rd.title}
            </h2>
            <p className="mt-6 text-xl font-semibold leading-8 underline">
              {texts.rd.subtitle}
            </p>
            <p>{texts.rd.content}</p>
            <h3 className="underline">{texts.rd.labtitle}</h3>
            <p>{texts.rd.labSubtitle}</p>
            <ul>
              {texts.rd.labArr.map((item, i) => (
                <li key={i} className="arrow-before">
                  {item}
                </li>
              ))}
            </ul>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              {[0, 1, 2].map((image) => (
                <GatsbyImage
                  key={image}
                  image={getImage(data[`labo_${image + 1}`])}
                  alt={data[`labo_${image + 1}`].name}
                  className="rounded-sm"
                />
              ))}
            </div>
          </section>

          {/* HR content */}
          <div
            id="rh"
            ref={sectionRefs["rh"]}
            className="mx-auto space-y-10 lg:mx-0"
          >
            <h2 className="my-4 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              {texts.hr.heroTitle}
            </h2>
            {texts.hr.sections.map((section, idx) => (
              <div key={idx} className="space-y-4">
                <h3 className="text-2xl font-bold">{section.title}</h3>
                {section.texts.map((text, idx) => (
                  <p key={idx}>{text}</p>
                ))}
                {section.list && (
                  <ul className="list-inside">
                    {section.list.map((item, idx) => (
                      <li key={idx} className="arrow-before">
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                {section.indicators && (
                  <div className="flex flex-col gap-y-5">
                    {section.indicators.map((el, i) => (
                      <div key={i} className="space-y-6 rounded-md p-7 shadow">
                        <h4 className="border-b-2 border-fehr-500 font-semibold">
                          {el.name}
                        </h4>
                        <ul className="list-inside list-disc space-y-2">
                          {el.indicators.map((li, i) => (
                            <li key={i}>{li}</li>
                          ))}
                        </ul>
                        <p className="text-lg">
                          <strong>{el.note}</strong>
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Button
              external
              buttonURL="https://www.talentdetection.com/fehr/65771-hdMCpqja1C/accueil"
              buttonString={texts.hr.buttonText}
            />
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default GroupTemplate
